/**
 * Promote Auth Check
 *
 * Probes the Promote `/whoami` endpoint to verify a valid session cookie.
 * If the session is missing, Promote responds with a 302 redirect to its
 * own login page — we use `redirect: 'manual'` to intercept this rather
 * than following it. An opaque redirect (type 'opaqueredirect', status 0)
 * or an explicit 401/403 both mean "not authenticated".
 *
 * Note: `redirect: 'manual'` causes Chrome DevTools to show the request
 * in red even on success (200). This is a cosmetic DevTools quirk — the
 * response is valid and correct.
 *
 * Server errors (5xx, network) are treated as non-blocking — Promote
 * features degrade gracefully rather than blocking the entire app.
 */

const SESSION_STORAGE_KEY = 'promote_auth_in_progress';

/**
 * Creates a session check function for Promote.
 *
 * @param baseUrl - The Promote service base URL (from tenant config `PROMOTE_BASE_URL`)
 * @returns A function that returns true if authenticated, false if a redirect was initiated
 */
export function createPromoteCheck(baseUrl: string): () => Promise<boolean> {
  return async (): Promise<boolean> => {
    try {
      // Use redirect: 'manual' so the browser doesn't silently follow
      // Promote's 302 → /login. Cross-origin manual redirects surface as
      // opaque redirect responses (type 'opaqueredirect', status 0).
      const response = await fetch(`${baseUrl}/whoami`, {
        credentials: 'include',
        redirect: 'manual',
      });

      // Opaque redirect (cross-origin 302) or explicit auth failure → not authenticated
      if (
        response.type === 'opaqueredirect' ||
        response.status === 0 ||
        response.status === 401 ||
        response.status === 403
      ) {
        sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
        window.location.href = `${baseUrl}/refresh?redirect=${encodeURIComponent(window.location.href)}`;
        return false;
      }

      if (response.ok) {
        return true;
      }

      // Non-auth failure (5xx, etc.) — don't block the app
      console.warn(
        `Promote /whoami returned ${response.status} — continuing without Promote auth`,
      );
      return true;
    } catch (error) {
      // Network error — don't block the app
      console.warn(
        'Promote auth check failed (network error) — continuing',
        error,
      );
      return true;
    }
  };
}

export { SESSION_STORAGE_KEY as PROMOTE_SESSION_STORAGE_KEY };
